/**
 * Free tool widget: character counter per social network. Pure client-side,
 * no API calls. Reuses the canonical limits from `lib/platforms.ts`.
 */
import { useMemo, useState } from 'react';
import { PLATFORM_CHAR_LIMITS } from '../../lib/platforms';

const NETWORKS: { key: keyof typeof PLATFORM_CHAR_LIMITS; label: string }[] = [
  { key: 'twitter', label: 'X (Twitter)' },
  { key: 'threads', label: 'Threads' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'facebook', label: 'Facebook' },
];

export function CharacterCounter() {
  const [text, setText] = useState('');

  const chars = useMemo(() => Array.from(text).length, [text]);
  const words = useMemo(() => (text.trim() ? text.trim().split(/\s+/).length : 0), [text]);

  return (
    <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
      <div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Escribe o pega aquí tu caption, tuit o descripción…"
          rows={9}
          className="w-full rounded-xl border border-slate-300 p-4 text-slate-900 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none resize-y"
        />
        <div className="mt-3 flex gap-6 text-sm">
          <span className="text-slate-500">
            Caracteres: <strong className="text-slate-900 tabular-nums">{chars}</strong>
          </span>
          <span className="text-slate-500">
            Palabras: <strong className="text-slate-900 tabular-nums">{words}</strong>
          </span>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <h3 className="text-sm font-semibold text-slate-900">Límite por red</h3>
        <ul className="mt-3 space-y-2">
          {NETWORKS.map(({ key, label }) => {
            const limit = PLATFORM_CHAR_LIMITS[key].standard ?? 0;
            const remaining = limit - chars;
            const over = remaining < 0;
            const pct = limit > 0 ? Math.min(100, (chars / limit) * 100) : 0;
            return (
              <li key={key}>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-700">{label}</span>
                  <span className={`tabular-nums font-medium ${over ? 'text-red-600' : 'text-slate-500'}`}>
                    {over ? `${remaining}` : `${remaining} restantes`}
                  </span>
                </div>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                  <div
                    className={`h-full rounded-full transition-all ${over ? 'bg-red-500' : 'bg-brand-500'}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
        <p className="mt-3 text-xs text-slate-400">
          X permite 25.000 caracteres con Premium.
        </p>
      </div>
    </div>
  );
}
